<script setup>
import { ref, watch, onUnmounted } from 'vue';
import { mountCardElement, unmountCardElement, confirmCard } from '../utils/stripe.js';

const props = defineProps({
    show: Boolean,
    total: Number,
    clientSecret: String
});

const emit = defineEmits(['close', 'success', 'error']);

const cardMounted = ref(false);
const processing = ref(false);
const cardholderName = ref('');
const nameError = ref('');
let cardElement = null;

watch(() => props.show, async (newVal) => {
    if (newVal && props.clientSecret) {
        await new Promise(resolve => setTimeout(resolve, 100));
        mountCard();
        cardholderName.value = '';
        nameError.value = '';
    } else if (!newVal) {
        unmountCard();
    }
});

function mountCard() {
    const container = document.getElementById('stripe-card-element');
    if (container && !cardMounted.value) {
        try {
            // Detectar modo oscuro (sistema o manual)
            const isSystemDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
            const isManualDark = document.documentElement.getAttribute('data-theme') === 'dark';
            const isDark = isManualDark || isSystemDark;

            cardElement = mountCardElement(container, isDark);
            cardMounted.value = true;
        } catch (err) {
            console.error('Error montando tarjeta:', err);
        }
    }
}

function unmountCard() {
    if (cardElement && cardMounted.value) {
        try {
            cardElement.unmount();
            cardElement = null;
            cardMounted.value = false;
        } catch (err) {
            console.error('Error desmontando tarjeta:', err);
        }
    }
}

async function handlePay() {
    if (!cardElement || !props.clientSecret) return;

    // Validar nombre del titular
    const name = cardholderName.value.trim();
    if (!name || name.length < 3) {
        nameError.value = 'Por favor ingresa el nombre del titular';
        return;
    }
    nameError.value = '';

    processing.value = true;

    try {
        const result = await confirmCard({
            clientSecret: props.clientSecret,
            card: cardElement,
            billingDetails: {
                name: name
            }
        });
        emit('success', result);
    } catch (err) {
        emit('error', err.message || 'Error al procesar el pago');
    } finally {
        processing.value = false;
    }
}

function handleCancel() {
    if (!processing.value) {
        emit('close');
    }
}

onUnmounted(() => {
    unmountCard();
});
</script>

<template>
    <Transition name="modal">
        <div v-if="show" class="modal-overlay" @click.self="handleCancel">
            <div class="modal-container">
                <!-- Header -->
                <header class="modal-header">
                    <h2 class="modal-title">Pago con tarjeta</h2>
                    <button class="close-btn" @click="handleCancel" :disabled="processing" aria-label="Cerrar">
                        <span class="material-symbols-rounded">close</span>
                    </button>
                </header>

                <!-- Body -->
                <div class="modal-body">
                    <!-- Total Amount -->
                    <div class="amount-section">
                        <span class="amount-label">Total a pagar</span>
                        <span class="amount-value">${{ total?.toFixed(2) || '0.00' }}</span>
                    </div>

                    <!-- Stripe Logo -->
                    <div class="powered-by">
                        <span>Procesado de forma segura por</span>
                        <svg class="stripe-logo" viewBox="0 0 60 25" xmlns="http://www.w3.org/2000/svg">
                            <path fill="#635BFF"
                                d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.9 0 1.85 6.29.97 6.29 5.88z" />
                        </svg>
                    </div>

                    <!-- Cardholder Name -->
                    <div class="input-section">
                        <label class="input-label">
                            <span class="material-symbols-rounded icon">person</span>
                            Nombre del titular
                        </label>
                        <input v-model="cardholderName" type="text" class="input-field"
                            :class="{ 'input-error': nameError }" placeholder="Nombre como aparece en la tarjeta"
                            :disabled="processing" @input="nameError = ''" />
                        <p v-if="nameError" class="error-message">
                            <span class="material-symbols-rounded">error</span>
                            {{ nameError }}
                        </p>
                    </div>

                    <!-- Card Element -->
                    <div class="card-section">
                        <label class="card-label">
                            <span class="material-symbols-rounded icon">credit_card</span>
                            Detalles de la tarjeta
                        </label>
                        <div id="stripe-card-element" class="card-element"></div>
                        <p class="card-hint">
                            <span class="material-symbols-rounded">lock</span>
                            Tus datos están protegidos con encriptación de 256 bits
                        </p>
                    </div>
                </div>

                <!-- Footer -->
                <footer class="modal-footer">
                    <button class="btn btn-cancel" @click="handleCancel" :disabled="processing">
                        Cancelar
                    </button>
                    <button class="btn btn-pay" @click="handlePay" :disabled="processing || !cardMounted">
                        <span v-if="processing" class="spinner"></span>
                        <span v-else>Pagar ${{ total?.toFixed(2) || '0.00' }}</span>
                    </button>
                </footer>
            </div>
        </div>
    </Transition>
</template>

<style scoped>
.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.6);
    backdrop-filter: blur(4px);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10000;
    padding: 1rem;
}

.modal-container {
    background: white;
    border-radius: 16px;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    width: 100%;
    max-width: 480px;
    overflow: hidden;
    animation: slideUp 0.3s ease-out;
}

@keyframes slideUp {
    from {
        opacity: 0;
        transform: translateY(20px);
    }

    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.modal-header {
    padding: 1.5rem 1.75rem;
    border-bottom: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: space-between;
}

.modal-title {
    font-size: 1.375rem;
    font-weight: 600;
    color: #1f2937;
    margin: 0;
}

.close-btn {
    background: transparent;
    border: none;
    color: #6b7280;
    cursor: pointer;
    padding: 0.5rem;
    border-radius: 8px;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    justify-content: center;
}

.close-btn:hover:not(:disabled) {
    background: #f3f4f6;
    color: #1f2937;
}

.close-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.modal-body {
    padding: 1.75rem;
}

.amount-section {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1.25rem;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 12px;
    margin-bottom: 1.5rem;
}

.amount-label {
    color: rgba(255, 255, 255, 0.9);
    font-size: 0.9rem;
    font-weight: 500;
}

.amount-value {
    color: white;
    font-size: 1.875rem;
    font-weight: 700;
    letter-spacing: -0.5px;
}

.powered-by {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    margin-bottom: 1.5rem;
    font-size: 0.8rem;
    color: #6b7280;
}

.stripe-logo {
    height: 18px;
    width: auto;
}

.input-section,
.card-section {
    margin-bottom: 1.25rem;
}

.input-label,
.card-label {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 600;
    color: #374151;
    margin-bottom: 0.75rem;
    font-size: 0.95rem;
}

.input-label .icon,
.card-label .icon {
    font-size: 20px;
    color: #635BFF;
}

.input-field {
    width: 100%;
    padding: 0.75rem 0.875rem;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    font-size: 0.9rem;
    transition: all 0.2s;
    background: white;
    color: #1f2937;
    font-family: inherit;
    box-sizing: border-box;
}

.input-field:focus {
    outline: none;
    border-color: #635BFF;
    box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.1);
}

.input-field:disabled {
    background: #f9fafb;
    cursor: not-allowed;
}

.input-field.input-error {
    border-color: #ef4444;
}

.input-field.input-error:focus {
    box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
}

.error-message {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-top: 0.5rem;
    font-size: 0.8rem;
    color: #ef4444;
    font-weight: 500;
}

.error-message .material-symbols-rounded {
    font-size: 16px;
}

.card-element {
    padding: 0.875rem;
    border: 1.5px solid #e5e7eb;
    border-radius: 10px;
    background: white;
    transition: all 0.2s;
    min-height: 44px;
    box-sizing: border-box;
}

.card-element:focus-within {
    border-color: #635BFF;
    box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.1);
}

.card-hint {
    display: flex;
    align-items: center;
    gap: 0.375rem;
    margin-top: 0.75rem;
    font-size: 0.8rem;
    color: #059669;
    font-weight: 500;
}

.card-hint .material-symbols-rounded {
    font-size: 16px;
}

.modal-footer {
    padding: 1.25rem 1.75rem;
    border-top: 1px solid #e5e7eb;
    display: flex;
    gap: 0.75rem;
    background: #f9fafb;
}

.btn {
    flex: 1;
    padding: 0.875rem 1.5rem;
    border-radius: 10px;
    font-weight: 600;
    font-size: 0.95rem;
    cursor: pointer;
    transition: all 0.2s;
    border: none;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
}

.btn-cancel {
    background: white;
    color: #374151;
    border: 1.5px solid #d1d5db;
}

.btn-cancel:hover:not(:disabled) {
    background: #f9fafb;
    border-color: #9ca3af;
}

.btn-pay {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: white;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-pay:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5);
}

.btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none !important;
}

.spinner {
    width: 18px;
    height: 18px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: white;
    border-radius: 50%;
    animation: spin 0.6s linear infinite;
}

@keyframes spin {
    to {
        transform: rotate(360deg);
    }
}

/* Transiciones del modal */
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}

.modal-enter-active .modal-container,
.modal-leave-active .modal-container {
    transition: transform 0.3s ease;
}

.modal-enter-from .modal-container,
.modal-leave-to .modal-container {
    transform: scale(0.9);
}

/* Responsivo */
@media (max-width: 640px) {
    .modal-container {
        max-width: 100%;
        border-radius: 16px 16px 0 0;
        align-self: flex-end;
    }

    .amount-value {
        font-size: 1.5rem;
    }

    .modal-footer {
        flex-direction: column-reverse;
    }

    .btn {
        width: 100%;
    }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
    .modal-overlay {
        background: rgba(0, 0, 0, 0.8);
    }

    .modal-container {
        background: #1e293b;
    }

    .modal-header {
        border-color: #334155;
    }

    .modal-title {
        color: #e2e8f0;
    }

    .close-btn {
        color: #94a3b8;
    }

    .close-btn:hover:not(:disabled) {
        background: #334155;
        color: #e2e8f0;
    }

    .powered-by {
        color: #94a3b8;
    }

    .input-label,
    .card-label {
        color: #e2e8f0;
    }

    .input-field {
        background: #0f172a;
        border-color: #334155;
        color: #e2e8f0;
    }

    .input-field:focus {
        border-color: #635BFF;
        box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.2);
    }

    .input-field:disabled {
        background: #1e293b;
    }

    .card-element {
        background: #0f172a;
        border-color: #334155;
    }

    .card-element:focus-within {
        border-color: #635BFF;
        box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.2);
    }

    .modal-footer {
        background: #0f172a;
        border-color: #334155;
    }

    .btn-cancel {
        background: #1e293b;
        color: #e2e8f0;
        border-color: #334155;
    }

    .btn-cancel:hover:not(:disabled) {
        background: #334155;
        border-color: #475569;
    }
}

:root[data-theme="dark"] {
    .modal-overlay {
        background: rgba(0, 0, 0, 0.8);
    }

    .modal-container {
        background: #1e293b;
    }

    .modal-header {
        border-color: #334155;
    }

    .modal-title {
        color: #e2e8f0;
    }

    .close-btn {
        color: #94a3b8;
    }

    .close-btn:hover:not(:disabled) {
        background: #334155;
        color: #e2e8f0;
    }

    .powered-by {
        color: #94a3b8;
    }

    .input-label,
    .card-label {
        color: #e2e8f0;
    }

    .input-field {
        background: #0f172a;
        border-color: #334155;
        color: #e2e8f0;
    }

    .input-field:focus {
        border-color: #635BFF;
        box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.2);
    }

    .input-field:disabled {
        background: #1e293b;
    }

    .card-element {
        background: #0f172a;
        border-color: #334155;
    }

    .card-element:focus-within {
        border-color: #635BFF;
        box-shadow: 0 0 0 3px rgba(99, 91, 255, 0.2);
    }

    .modal-footer {
        background: #0f172a;
        border-color: #334155;
    }

    .btn-cancel {
        background: #1e293b;
        color: #e2e8f0;
        border-color: #334155;
    }

    .btn-cancel:hover:not(:disabled) {
        background: #334155;
        border-color: #475569;
    }
}
</style>
